import React, { useMemo } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { useLocationControl, useUserLocation } from '@/hooks/use-user-location';
import { useNearbyFeed } from '@/features/activity/hooks/use-activity';
import { useLikeCheckIn } from '@/features/activity/hooks/use-like-check-in';
import { FeedList } from '@/features/activity/components/feed-list';
import {
  LocationFailureStatus,
  LocationUnavailablePrompt,
} from '@/features/activity/components/location-unavailable-prompt';
import { Text } from '@/components/ui/text';
import { useAppTheme } from '@/providers/theme-provider';

export function NearbyFeedTab() {
  const { status, coords } = useUserLocation();
  const { refresh, isRefreshing } = useLocationControl();
  const theme = useAppTheme();
  const { toggleLike } = useLikeCheckIn();

  if (status === 'idle' || status === 'requesting') {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (status !== 'granted' || !coords) {
    // A feed that calls itself "Nearby" must not seed itself with a default
    // location the way the ranking-only screens do -- that would present
    // far-away check-ins as nearby with no signal anything went wrong.
    const failure: LocationFailureStatus = status === 'granted' ? 'unavailable' : status;
    return (
      <LocationUnavailablePrompt
        status={failure}
        onRetry={refresh}
        isRetrying={isRefreshing}
      />
    );
  }

  return (
    <NearbyFeedContent
      latitude={coords.latitude}
      longitude={coords.longitude}
      onLike={toggleLike}
    />
  );
}

interface NearbyFeedContentProps {
  latitude: number;
  longitude: number;
  onLike: (checkInId: string, currentlyLiked: boolean) => void;
}

function NearbyFeedContent({ latitude, longitude, onLike }: NearbyFeedContentProps) {
  const {
    data,
    isLoading,
    error,
    refetch,
    isRefetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useNearbyFeed(latitude, longitude);

  const theme = useAppTheme();

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  if (error) {
    return (
      <View className="flex-1 items-center justify-center px-8">
        <Text className="text-sm text-center text-muted-foreground">
          Something went wrong. Pull down to try again.
        </Text>
      </View>
    );
  }

  return (
    <FeedList
      items={items}
      hasNextPage={hasNextPage ?? false}
      isFetchingNextPage={isFetchingNextPage}
      isRefetching={isRefetching}
      onLoadMore={() => fetchNextPage()}
      onRefresh={() => refetch()}
      onLike={onLike}
      ListEmptyComponent={
        <View className="flex-1 items-center justify-center px-8 py-16">
          <Text className="text-xl font-semibold text-center mb-2 text-foreground">
            No activity nearby
          </Text>
          <Text className="text-sm text-center text-muted-foreground">
            Check back later or try the Friends tab.
          </Text>
        </View>
      }
    />
  );
}
